<template>
  <section class="home-page">
    <div class="">
      <div class="landing columns is-centered is-fullheight">
        <div class="text">
          <h1 class="_logo" @click="handleClick">
            Bicycle insurance,<br />
            trusted by thousands
          </h1>
          <h2 class="ride-smart" @click="handleClick">
            Ride smart with Pedalsure
          </h2>
          <p class="hero-intro">
            Specialist cycle insurance for UK cyclists: bicycle insurance for
            road, mountain, commuter, cargo and electric bikes, built around the
            cycling you do.
          </p>

          <TrustList />

          <button
            class="button ps-button get-quote-bt-custom get-quote-bt my-4"
            @click="handleClick"
          >
            <span>Get your personal quote</span>
          </button>
        </div>
      </div>
      <div class="container">
        <Featured />
        <CanPedalsure />
        <CoverSummary />
        <SiteTrust />
        <TypeInsurances />
        <PriceComparison />

        <Cycling />
        <PromotionBanner />
        <BlogWidgetHorizontal
          title="Learn more about cycling insurance"
          :slugs="[
            'which-cycling-insurance-is-best-for-me',
            'does-home-insurance-cover-my-bike',
            'do-cyclists-have-to-have-insurance',
          ]"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import Featured from '@/components/homepage/Featured'
import CanPedalsure from '@/components/homepage/CanPedalsure'
import CoverSummary from '@/components/homepage/CoverSummary'
import { RATING_LABEL, RATING_VALUE } from '@/helpers/rating'
import Cycling from '@/components/homepage/Cycling'
import PriceComparison from '@/components/homepage/PriceComparison'
import PromotionBanner from '@/components/PromotionBanner'
import TrustList from '@/components/homepage/TrustList'
import BlogWidgetHorizontal from '@/components/blog-widget/BlogWidgetHorizontal'
import TypeInsurances from '@/components/homepage/TypeInsurances'
import SiteTrust from '@/components/homepage/SiteTrust'
import { buildSocialMeta, resolveSocialImage } from '@/helpers/social-meta'
import heroLeftIllustration from '~/assets/images/illustrations/Homepage-Hero-Left-side.svg'
import { productOffer } from '@/helpers/product-offer'

// og:image and Product.image both read this, so the two cannot drift apart.
const OG_IMAGE_PATH = '/images/Homepage-Hero-Illustration.png'

export default {
  name: 'HomePage',
  components: {
    CanPedalsure,
    CoverSummary,
    Cycling,
    PriceComparison,
    Featured,
    PromotionBanner,
    TrustList,
    BlogWidgetHorizontal,
    TypeInsurances,
    SiteTrust,
  },
  head() {
    const title = 'Bicycle & Cycle Insurance UK | Including E-Bikes | Pedalsure'
    const description = `Specialist cycle insurance for UK riders: theft and accidental damage cover for bikes and e-bikes from £2.47 a month. Rated ${RATING_LABEL}, ${RATING_VALUE} out of 5.`
    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'title', name: 'title', content: title },
        {
          hid: 'keywords',
          name: 'keywords',
          content:
            'cycling insurance, insurance for cyclists, insurance for bicycles, bicycle insurance',
        },
        // og:type stays 'article' here, matching the value this page has
        // always published - not part of the bug this fixes, and changing it
        // isn't worth the risk on a page that already validates correctly.
        ...buildSocialMeta({
          title,
          description,
          path: '/',
          image: OG_IMAGE_PATH,
          type: 'article',
        }).meta,
      ],
      link: [
        // The hero's .landing::before illustration is the page's LCP element
        // whenever the cookie banner isn't showing. As a CSS background it is
        // only discovered after style calc, so it arrives after first paint and
        // then waits for the app bundle to finish before it can be painted —
        // ~700 ms after the H1 on a throttled phone. Preloading it lets it
        // paint in the same frame as the hero text.
        {
          hid: 'preload-hero-illustration',
          rel: 'preload',
          as: 'image',
          type: 'image/svg+xml',
          href: heroLeftIllustration,
          fetchpriority: 'high',
        },
      ],
      script: [
        {
          innerHTML: `
        {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Pedalsure Bicycle Insurance",
          "description":
            "Specialist bicycle insurance for UK cyclists: theft and accidental damage cover, with optional personal accident and liability cover.",
          "image": ${JSON.stringify([resolveSocialImage(OG_IMAGE_PATH)])},
          "brand": {
            "@type": "Brand",
            "name": "Pedalsure"
          },
          "offers": ${JSON.stringify(productOffer())}
        }
        `,
          type: 'application/ld+json',
        },
        {
          innerHTML: `
        {
          "@context": "https://schema.org",
          "@type": "Corporation",
          "name": "Pedalsure",
          "url": "https://www.pedalsure.com",
          "logo": "https://www.pedalsure.com/Pedalsure-Logo_Large.svg",
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "0800 888 6745",
            "contactType": "customer service",
            "areaServed": "GB",
            "availableLanguage": "en"
          },
          "sameAs": [
            "https://www.facebook.com/pedalsure/",
            "https://www.instagram.com/ridepedalsure/",
            "https://twitter.com/pedalsure"
          ]
        }
        `,
          type: 'application/ld+json',
        },
      ],
    }
  },
  mounted() {
    if (this.$route.query.type === 'paid') {
      this.$modal.show('congrats-modal', {
        buttonText: 'Continue to home page',
        url: '/',
      })
    }
    if (this.$route.query.getQuote) {
      this.handleClick()
    }
  },
  methods: {
    ...mapActions('quote', {
      resetQuote: 'resetQuote',
    }),
    handleClick() {
      this.resetQuote()
      this.$modal.show('quick-quote')
    },
  },
}
</script>

<style lang="scss">
.container {
  max-width: 1100px;
  margin: 0 auto;

  @include tablet {
    padding: 0 20px;
  }
}

._logo {
  font-size: 2rem;
  line-height: 3rem;
  color: $ps-primary;
  text-align: center;
  letter-spacing: 0px;

  @include tablet {
    font-size: 3rem;
    line-height: 4rem;
  }
}

// Sits between the tagline and the trust list, so it pulls up into the
// tagline's bottom padding rather than adding a second gap.
.hero-intro {
  max-width: 420px;
  margin: -14px auto 24px;
  padding: 0 4px;
  text-align: center;
  font-size: $ps-text-small;
  line-height: 1.5;
  color: $ps-body-color;
}

.ride-smart {
  color: $black;
  font-size: 1.3rem;
  line-height: 1.1rem;
  text-align: center;
  color: $ps-gray;
  padding: 15px 0px 30px;
  font-family: $family-light;

  @include tablet {
    font-size: 1.4rem;
    line-height: 1.5rem;
  }
}

.trust-pilot-image {
  text-align: center;
  margin: 10px 0 50px 0;

  p {
    font-size: 0.8rem;
  }

  @include tablet {
    margin-bottom: 10px 0 30px 0;
  }

  img {
    width: 120px;
    height: auto;

    @include tablet {
      width: 160px;
    }
  }
}

.text {
  z-index: 4;
}

.get-quote-bt-custom {
  display: block;
  margin: 0 auto;
  font-size: 1rem !important;
  min-width: 170px;
  text-align: center;
  padding: 0;
  z-index: 1;
  height: 33px;
  padding: 0 10px;
}

.landing {
  position: relative;
  // Was 500px. The intro paragraph under the tagline (PS-136) adds ~106px of
  // text on a phone and ~82px from tablet up; the hero grows by the same
  // amount so the bottom-anchored illustrations keep the clearance they had
  // from the trust list and button, instead of running under them.
  min-height: 610px;
  // background-image: url("~assets/images/illustrations/Homepage-Hero-Illustration.png");
  margin: 20px 0;

  @include tablet {
    min-height: 585px;
    margin: 0;
  }

  &:before,
  &:after {
    content: '';
    position: absolute;
    width: 330px;
    height: 200px;
    bottom: 0;
    z-index: -1;
    background-repeat: no-repeat;

    @include tablet {
      width: 600px;
      height: 350px;
    }

    @include desktop {
      width: 730px;
      height: 450px;
    }

    @include widescreen {
      width: 900px;
      height: 420px;
    }
  }

  &:before {
    background-image: url('~assets/images/illustrations/Homepage-Hero-Left-side.svg');
    left: 0;
    background-position: -80px 0;

    @include tablet {
    }

    @include desktop {
      background-position: -100px 0;
    }
  }

  &:after {
    background-image: url('~assets/images/illustrations/Commuter.svg');
    right: 0;
    background-position: 150px 0;

    @include tablet {
      background-position: 260px -25px;
    }

    @include desktop {
      background-position: 300px -25px;
    }

    @include widescreen {
      background-position: 400px -25px;
    }
  }
}

.blog-widget-title {
  border: 0 !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style>
